import { ref } from 'vue'
import type { FeeOverridePayload, OrganisationFeeOverride } from '@/types/pricing'
import {
  clearOrganisationFeeOverride,
  fetchOrganisationFeeOverride,
  updateOrganisationFeeOverride,
} from '@/api/pricing'

export function useOrganisationFeeOverride() {
  const override = ref<OrganisationFeeOverride | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const actionLoading = ref(false)
  const actionError = ref<string | null>(null)
  const fieldErrors = ref<Record<string, string[]>>({})

  // GET /admin/organisations/{organisation}/fee-override — reuses
  // PlatformFeeService::resolveFor() server-side, so the resolution order
  // is never re-derived on the client (spec §5.3.3 / issue #153 follow-on).
  async function fetchOverride(organisationId: number): Promise<void> {
    loading.value = true
    error.value = null
    try {
      override.value = await fetchOrganisationFeeOverride(organisationId)
    } catch {
      error.value = 'admin.common.error_loading'
    } finally {
      loading.value = false
    }
  }

  async function setOverride(organisationId: number, payload: FeeOverridePayload): Promise<boolean> {
    actionLoading.value = true
    actionError.value = null
    fieldErrors.value = {}
    try {
      override.value = await updateOrganisationFeeOverride(organisationId, payload)
      return true
    } catch (e: unknown) {
      if (is422Error(e)) {
        fieldErrors.value = extractFieldErrors(e) ?? {}
      } else {
        actionError.value = 'admin.common.error_generic'
      }
      return false
    } finally {
      actionLoading.value = false
    }
  }

  async function clearOverride(organisationId: number, reason: string): Promise<boolean> {
    actionLoading.value = true
    actionError.value = null
    fieldErrors.value = {}
    try {
      override.value = await clearOrganisationFeeOverride(organisationId, reason)
      return true
    } catch (e: unknown) {
      if (is422Error(e)) {
        fieldErrors.value = extractFieldErrors(e) ?? {}
      } else {
        actionError.value = 'admin.common.error_generic'
      }
      return false
    } finally {
      actionLoading.value = false
    }
  }

  return {
    override,
    loading,
    error,
    actionLoading,
    actionError,
    fieldErrors,
    fetchOverride,
    setOverride,
    clearOverride,
  }
}

function is422Error(e: unknown): boolean {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: { status: number } }).response.status === 422
  )
}

function extractFieldErrors(e: unknown): Record<string, string[]> | null {
  if (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object'
  ) {
    const resp = (e as { response: { data?: { errors?: Record<string, string[]> } } }).response
    return resp.data?.errors ?? null
  }
  return null
}
